import { reportBelongsToSite } from '../../lib/reportSiteIdentity';
import { resolveSiteStudyArea } from '../../lib/siteStudyArea';
/**
 * ReportPreferencesStep — second stage of NewReportWizard.
 *
 * Renders the per-type preference sub-form, a real preflight panel
 * (Workflow Prompt 8), and a Generate CTA gated on both form-required
 * fields AND preflight section availability. Admins with the override
 * toggle on can bypass the preflight gate.
 */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { FileText, Leaf, Building2, Zap, Droplets, Cable, AlertTriangle, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { useStore } from '../../store';
import { getReportType, type ReportTypeId } from '../../config/reportTypes';
import {
  DEFAULT_GENERATION_TIER,
  type GenerationTier,
} from '../../config/generationTier';
import type { SiteEntity } from '../../types/site';
import type { SiteRagReport, TypedPreferencesByType } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import { runPreflight, preflightRedHelp, isFirstReportForSite, type PreflightResult } from '../../lib/preflightCheck';
import { getPreflightMode, type PreflightMode } from '../../lib/preflightMode';
import { FirstRunBootstrapCard } from './FirstRunBootstrapCard';
import { getEffectiveWiredProviders } from '../../config/wiredProviders';
import { loadSedById } from '../../services/sedLoader';
import { type CompanyArchetype } from '../../types/company';
import { autoRecommendedRefs } from '../../lib/autoRecommendedRefs';
import { DocumentLibrary } from './DocumentLibrary';
import { isLibraryIndexDoc } from '../../services/knowledge/libraryIndex';
import { formatRelativeTime } from '../../lib/portfolio/format';
import {
  evaluateSedBootstrapFreshness,
  resolveFreshnessThresholdMs,
} from '../../lib/sed/sedBootstrapCoordinator';
import { PreferencesFormFor, validatePreferencesFor } from './preferences';
import { WizardSetupReviewPanel, type WizardSetupReviewPanelHandle } from './WizardSetupReviewPanel';
import { resolveWizardPreferencesForType } from '../../lib/coverage/resolveReportSetupFields';
import { useEffectiveSiteConfig } from './useEffectiveSiteConfig';
import { crossFieldWarnings, sanitizePreferencesForCommit } from './preferences/crossFieldWarnings';
import { labelFor } from '../../lib/reportPreferences/labels';
import type { ValidationResult } from './preferences/shared';
import { emitAuditEvent } from '../../lib/auditLogClient';
import { type ReadinessRowStatus, type ReadinessRequiredRow } from './DataReadinessPanel';
import { SED_SECTION_LABELS } from '../../lib/sedStaleness';
import { SedCrossTabWaitBanner } from './SedCrossTabWaitBanner';

const ICON_MAP: Record<string, React.ElementType> = { FileText, Leaf, Building2, Zap, Droplets, Cable };

/**
 * DR4 — human-readable provider name + a remediation hint per SED
 * section, surfaced on 'queried-empty' rows in the DataReadinessPanel
 * so "no data returned" names *which* provider was queried and says
 * what the report will do without it. Keep the provider names aligned
 * with `SECTION_PRIMARY_PROVIDER` / `preflightRedHelp` in
 * lib/preflightCheck.ts. Sections not in this map fall back to the
 * panel's generic "Provider returned no data … data not available
 * note" copy.
 */
const READINESS_PROVIDER_INFO: Record<string, { providerName: string; noDataRemediation: string }> = {
  climate:       { providerName: 'Open-Meteo (climate)',          noDataRemediation: 'The Climate section will note this gap and rely on regional climate norms.' },
  waterStress:   { providerName: 'Aqueduct (water stress)',       noDataRemediation: 'The Water Resources section will note this gap and infer from regional context.' },
  seismic:       { providerName: 'USGS (seismic hazard)',         noDataRemediation: 'The Seismic section will note this gap; upload a site-specific PSHA to fill it.' },
  grid:          { providerName: 'HIFLD (grid infrastructure)',   noDataRemediation: 'The Grid section will note this gap; a utility service letter fills it precisely.' },
  flood:         { providerName: 'FEMA NFHL (flood)',             noDataRemediation: 'The Flood section will note this gap; an elevation certificate fills it precisely.' },
  environmental: { providerName: 'USFWS / NWI (environmental)',   noDataRemediation: 'The Environmental section will note this gap; a Phase I ESA fills it.' },
  nuclearInfra:  { providerName: 'NRC (nuclear infrastructure)',  noDataRemediation: 'The Nuclear Infrastructure section will note this gap.' },
  solar:         { providerName: 'NREL (solar resource)',         noDataRemediation: 'The Solar section will note this gap; an irradiance study fills it.' },
  wind:          { providerName: 'NREL (wind resource)',          noDataRemediation: 'The Wind section will note this gap; a wind study fills it.' },
  electricity:   { providerName: 'EIA (electricity rates)',       noDataRemediation: 'The Electricity section will note this gap; a utility tariff fills it.' },
  airQuality:    { providerName: 'EPA AirNow (air quality)',      noDataRemediation: 'The Air Quality section will note this gap; an attainment letter fills it.' },
  connectivity:  { providerName: 'PeeringDB (connectivity)',      noDataRemediation: 'The Connectivity section will note this gap; a fiber survey / carrier quote fills it.' },
  parcel:        { providerName: 'Regrid (parcel)',               noDataRemediation: 'The Parcel section will note this gap; a survey / title document fills it.' },
  regulatory:    { providerName: 'AI-derived (regulatory)',       noDataRemediation: 'The Regulatory section will note this gap; upload state regulatory filings to fill it.' },
  siteResearch:  { providerName: 'Site research provider',        noDataRemediation: 'The Site Research section will note this gap and proceed with the rest of the evidence base.' },
};

export type WizardMode = 'new' | 'update' | 'replace' | 'add-another';

interface Props {
  reportType: ReportTypeId;
  site: SiteEntity;
  existingReport?: SiteRagReport;
  initialPreferences?: TypedPreferencesByType[ReportTypeId];
  mode: WizardMode;
  onBack: () => void;
  /**
   * The optional second arg carries a per-report Reader-Profile override
   * captured in the preflight panel. Undefined means "use the company
   * default"; defined means "apply this archetype to the prompt for this
   * report only". Threaded through to generateReportFromSed via
   * options.companyArchetypeOverride.
   *
   * The third arg carries the user's document selection (Documents_On_Site
   * Prompt 4): the documentRefs[] the wizard ended up with plus the
   * documentSelectionMode ('auto' | 'mixed' | 'manual'). Both are saved
   * verbatim onto the generated report so getReportDocuments can resolve
   * them at read time.
   */
  onConfirm: (
    preferences: TypedPreferencesByType[ReportTypeId],
    companyArchetypeOverride?: CompanyArchetype,
    documentSelection?: { documentRefs: NonNullable<SiteRagReport['documentRefs']>; documentSelectionMode: 'auto' | 'mixed' | 'manual' },
    /**
     * Site_Configuration_Consolidation SC2-5 — fourth arg flags whether
     * the user toggled "Override for this report only" on. When true,
     * NewReportWizard threads the override through generateTypedReport's
     * `siteConfigOverrides.typedPreferencesOverride` so the report
     * records the override + the *AtGen snapshot matches the override
     * (not the site default). When false, the report inherits and
     * `typedPreferencesOverride` stays undefined — *AtGen matches the
     * site's current state.
     */
    overrideEnabled?: boolean,
    /** True when Generate was clicked while bootstrap/retry was in flight. */
    queuedAfterEvidence?: boolean,
    /**
     * Cost P3 — the generation tier the user picked in the wizard. DEFAULT is
     * 'investment-grade' (today's full-quality path, no behaviour change). When
     * the user opts into 'draft', the wizard threads it into
     * `generateTypedReport`'s `siteConfigOverrides.generationTier`, which the
     * P3 pipeline already consumes (Flash review, fewer iterations, judges off)
     * and stamps onto `report.generationTier` / `report.isDraft`. Drafts are
     * truth-gated + export-barred — for iteration, not delivery.
     */
    generationTier?: import('../../config/generationTier').GenerationTier,
    sectionColumnLayouts?: Record<string, 1 | 2 | 3>,
  ) => void;
  /**
   * Workflow Prompt 4 — fired whenever the user makes a state edit
   * (preferences, document selection, archetype override). The
   * wizard tracks dirty state and uses it to decide whether a
   * cancel attempt should fall through to the close handler or
   * route through a "Discard your changes?" confirm dialog.
   *
   * Categories the field arg can take: 'preferences' | 'documents'
   * | 'archetype'. The wizard de-dupes via Set-membership, so
   * repeated calls under the same field don't inflate the count.
   */
  onDirty?: (field: 'preferences' | 'documents' | 'archetype') => void;
  /**
   * Workflow Prompt 6 — origin of the prefilled preference values.
   * Drives the subtitle that tells the user WHERE the seed came
   * from so they don't have to remember which lineage they're on.
   *   'parent'  — `update` / `replace` mode, seeded from the
   *               existingReport.
   *   'sibling' — `add-another` mode, seeded from the most recent
   *               same-type sibling on this site.
   *   'intent'  — fresh seed from site.projectIntent (no
   *               prior report or no sibling preferences).
   */
  seedSource?: 'parent' | 'sibling' | 'intent';
  /**
   * The same-type sibling the wizard seeded from when seedSource =
   * 'sibling'. Carries the report id + createdAt so the subtitle
   * can label "Pre-filled from your previous … report (Apr 12)".
   */
  siblingReport?: SiteRagReport | null;
  /**
   * Site-default intent values, computed once by the wizard. The
   * "Reset to site defaults" link replaces the form state with this
   * exact object — that way "reset" is a single-step revert, not a
   * recompute that risks drifting from what the wizard would have
   * seeded for a fresh run.
   */
  intentDefaults?: TypedPreferencesByType[ReportTypeId];
  /** Opens Site Project Intent editor (Coverage Matrix parity). */
  onOpenSiteIntent?: () => void;
  /** Navigates to Coverage Matrix tab for inline setup edits. */
  onOpenCoverageMatrix?: () => void;
}

/** Report types that require per-run wizard input in simplified mode. */
const WIZARD_INLINE_SETUP_TYPES = ['custom-brief', 'executive-summary'] as const satisfies readonly ReportTypeId[];

function isWizardInlineSetupType(
  reportType: ReportTypeId,
): reportType is (typeof WIZARD_INLINE_SETUP_TYPES)[number] {
  return (WIZARD_INLINE_SETUP_TYPES as readonly ReportTypeId[]).includes(reportType);
}

const MODE_BANNER: Record<WizardMode, { label: string; tone: 'primary' | 'warning' }> = {
  'new':         { label: 'New report',                     tone: 'primary' },
  'update':      { label: 'Updating existing report',       tone: 'warning' },
  'replace':     { label: 'Replacing existing report',      tone: 'warning' },
  'add-another': { label: 'Adding another report version',  tone: 'primary' },
};

function dispatchValidator(reportType: ReportTypeId, prefs: any): ValidationResult {
  return validatePreferencesFor(reportType, prefs);
}

export const ReportPreferencesStep: React.FC<Props> = (props) => {
  try { resolveSiteStudyArea(props.site); }
  catch (error) { return <div className="space-y-3 p-5" role="alert"><p>{(error as Error).message}</p><p>Open the site map to review the study area.</p><Button onClick={props.onBack}>Back</Button></div>; }
  return <ReportPreferencesStepBody {...props} />;
};

const ReportPreferencesStepBody: React.FC<Props> = ({
  reportType, site, existingReport, initialPreferences, mode, onBack, onConfirm, onDirty,
  seedSource = 'intent', siblingReport, intentDefaults,
  onOpenSiteIntent, onOpenCoverageMatrix,
}) => {
  const typeDef = getReportType(reportType);
  const screeningRerun = reportType === 'feasibility'
    && Boolean(existingReport)
    && (mode === 'update' || mode === 'replace');
  const Icon = ICON_MAP[typeDef.icon] || FileText;
  const setupFlushRef = useRef<WizardSetupReviewPanelHandle>(null);
  const [prefs, setPrefsRaw] = useState<any>(() => {
    const base = initialPreferences ?? {};
    if (isWizardInlineSetupType(reportType)) {
      const merged = resolveWizardPreferencesForType(reportType, site) ?? {};
      return { ...merged, ...base };
    }
    return base;
  });
  // Setter shim — every user-driven prefs change rings the dirty
  // bell so the wizard's cancel-confirm flow knows there's work to
  // protect. The per-type form components are stateless and only
  // fire onChange from real user events, so we don't need to guard
  // against mount-time false positives.
  const setPrefs = React.useCallback((next: any) => {
    setPrefsRaw(next);
    onDirty?.('preferences');
  }, [onDirty]);
  // Per-report-only override toggle. Default OFF — wizard's primary mode
  // is "inherit from site"; override is the explicit opt-out.
  const [overrideEnabled] = useState<boolean>(false);
  const [sectionColumnLayouts] = useState<Record<string, 1 | 2 | 3>>(() => {
    return existingReport?.sectionColumnLayouts ?? {};
  });
  // Cost P3 — generation tier the user picks before launching. Default is the
  // full-quality investment-grade path (no behaviour change); 'draft' opts into
  // the cheaper iteration path (Flash review, fewer passes, judges off). Both
  // tiers run the deterministic correctness gates; a draft is export-barred and
  // banner-stamped. Threaded through onConfirm → siteConfigOverrides.generationTier.
  // Pinned to investment-grade — the Draft-tier picker was removed (2026-06-16);
  // `generationTier` is still read + threaded through onConfirm (no setter).
  const [generationTier] = useState<GenerationTier>(DEFAULT_GENERATION_TIER);
  const [sed, setSed] = useState<SiteEvidenceDocument | null>(null);
  const [sedLoading, setSedLoading] = useState(true);
  // Validation + cross-field warnings run on the value that will
  // actually be committed: inherited prefs when override OFF, form
  // state when override ON. Inherited values were saved through the
  // same validators on the Overview tab; treating them as valid here
  // keeps the wizard from blocking users on prefs they already
  // committed once.
  const wizardMergedPrefs = useMemo(
    () => resolveWizardPreferencesForType(reportType, site, sed) ?? {},
    [reportType, site.projectIntent, site.typedPreferencesDefaults, sed],
  );
  const simplifiedInlineSetup = isWizardInlineSetupType(reportType);
  const wizardEditablePrefs = simplifiedInlineSetup
    ? { ...wizardMergedPrefs, ...prefs }
    : wizardMergedPrefs;
  const prefsForValidation = simplifiedInlineSetup ? wizardEditablePrefs : wizardMergedPrefs;
  const validation = dispatchValidator(reportType, prefsForValidation);
  // Workflow Prompt 11 — multi-field warnings (KPI weights summing
  // to ~1, amber threshold < red, bandwidth in range, …). Errors
  // block the Generate button; warnings are advisory.
  const crossWarnings = useMemo(
    () => crossFieldWarnings(reportType, prefsForValidation),
    [reportType, prefsForValidation],
  );
  const crossErrors = crossWarnings.filter(w => w.severity === 'error');
  const crossWarnsOnly = crossWarnings.filter(w => w.severity === 'warn');
  const banner = MODE_BANNER[mode];

  // `allReports` is read by `bootstrapMode = isFirstReportForSite(site, allReports)`
  // below — keep the broad selector for that one consumer. The SED-loading
  // effect uses the narrower `latestSedRefId` selector defined further
  // down (Fix_React_Error_185_Prompt step 4) so it doesn't fire on
  // unrelated report mutations.
  const allReports = useStore(s => s.siteRag.reports);
  // Per-report Reader-Profile override. null means "fall through the
  // resolution chain (report → site → company)"; a concrete value
  // pins the report to that archetype regardless of site/company.
  // Threaded through onConfirm to companyArchetypeOverride; the store
  // applies the chain at generation time.
  const [archetypeOverride] = useState<CompanyArchetype | null>(null);
  const saveSiteCustomBrief = useStore(s => s.saveSiteCustomBrief);
  const deleteSiteCustomBrief = useStore(s => s.deleteSiteCustomBrief);

  // Document selection (Documents_On_Site Prompt 4). Seeded from the
  // auto-recommendation derived from per-report-type relevance; the
  // panel emits the user's edits + a mode tag ('auto' | 'mixed' | 'manual')
  // which both ride along into generateTypedReport.
  const [documentRefs] = useState<NonNullable<SiteRagReport['documentRefs']> | undefined>(undefined);
  const [documentSelectionMode] = useState<'auto' | 'mixed' | 'manual'>('auto');
  const effectiveDocumentRefs = documentRefs ?? autoRecommendedRefs(site, reportType);
  const docsCount = (site.documents ?? []).filter(d => !d.tags?.includes('library-index')).length;
  // Documents_On_Site Prompt 8-E — side-drawer DocumentLibrary opened
  // from the wizard's "Document evidence" panel. Mounted at a higher
  // z-index than the wizard itself so the user can upload without
  // losing their preferences.
  const [docDrawerOpen, setDocDrawerOpen] = useState(false);


  // Preflight Prompt 1/3 — discrete state machine. Lifted up to the
  // top of the component so handlers like the critical-gap gate can
  // skip noisy dialogs on first-run, and so the render branch can
  // short-circuit to the welcome card without re-reading the args.
  // Pure / memoised — recomputes only when site, sed, or the global
  // reports slice changes.
  const preflightMode: PreflightMode = useMemo(
    () => getPreflightMode({ site, sed, reports: allReports }),
    [site, sed, allReports],
  );

  const companySedBootstrapFreshnessThresholdMs = useStore(s => s.companySedBootstrapFreshnessThresholdMs);
  const sedBootstrapFreshness = useMemo(() => {
    const thresholdMs = resolveFreshnessThresholdMs(
      undefined,
      companySedBootstrapFreshnessThresholdMs,
    );
    return evaluateSedBootstrapFreshness(site, thresholdMs);
  }, [site, companySedBootstrapFreshnessThresholdMs]);

  // Opening preferences only reads saved evidence. Source refresh and
  // publication belong to an explicit evidence action or the confirmed
  // generation job; cancelling this dialog must not leave a writer running.

  // Failed-SED preflight banner. The bootstrap pipeline parks failures
  // by writing site.sedStatus='failed' (sedBootstrap.ts:288/388) along
  // with sedFailureReason. Surfacing the explicit branch + a Retry CTA
  // beats the previous behaviour where loadSedById(null) → null →
  // runPreflight(null, …) → generic red preflight that didn't tell the
  // user to hit Retry.
  const retrySEDBootstrap = useStore(s => s.retrySEDBootstrap);
  const [isRetrying, setIsRetrying] = useState(false);
  // Watch for sedStatus flips. 'failed' → anything else clears the
  // retry indicator and lets the form fall through to the normal
  // preflight path. The dep is `site.sedStatus` (a primitive), so the
  // effect doesn't fire on unrelated site mutations.
  useEffect(() => {
    if (site.sedStatus !== 'failed' && site.sedStatus !== 'bootstrapping') {
      setIsRetrying(false);
    }
  }, [site.sedStatus]);
  const handleSedRetry = async () => {
    setIsRetrying(true);
    try {
      await retrySEDBootstrap(site.id);
    } catch (e) {
      setIsRetrying(false);
      // toast handled by the store action's caller in SiteLandingPage; the
      // wizard surface relies on the banner re-rendering with the new
      // sedFailureReason rather than its own toast.
      console.warn('[ReportPreferencesStep] retry failed:', (e as Error)?.message);
    }
  };
  const sedFailed = site.sedStatus === 'failed';
  const evidenceCollecting = site.sedStatus === 'bootstrapping' || isRetrying;
  const blockGenerateForFailedSed = sedFailed && !evidenceCollecting;

  // Workflow Prompt 11 — sanitize prefs at the commit boundary. The
  // form keeps user-friendly literals like `airBasinOverride: 'auto'`
  // while the user is editing; the prompt builder expects undefined for
  // "user didn't decide" and clamped numeric ranges. Doing it here
  // means every onConfirm path (preflight refresh-and-generate,
  // preflight skip-refresh, the direct Generate click) flows through
  // the same shape fix. (SSOT removed the WaterResource.coolingType
  // sanitization — the field no longer exists on prefs.)
  const commitPrefs = (): TypedPreferencesByType[ReportTypeId] => {
    if (isWizardInlineSetupType(reportType)) {
      return sanitizePreferencesForCommit(reportType, wizardEditablePrefs)!;
    }
    return sanitizePreferencesForCommit(
      reportType,
      (resolveWizardPreferencesForType(
        reportType,
        useStore.getState().siteRag.sites.find(s => s.id === site.id) ?? site,
        sed,
      ) ?? {}) as never,
    )!;
  };

  const flushInlineSetupIfNeeded = async (): Promise<boolean> => {
    if (isWizardInlineSetupType(reportType)) return true;
    return setupFlushRef.current?.flushInlineSetup() ?? true;
  };

  const commitAndConfirm = async (queued = evidenceCollecting) => {
    const flushed = await flushInlineSetupIfNeeded();
    if (!flushed) return;
    onConfirm(
      commitPrefs(),
      archetypeOverride ?? undefined,
      { documentRefs: effectiveDocumentRefs, documentSelectionMode },
      overrideEnabled,
      queued,
      generationTier,
      sectionColumnLayouts,
    );
  };

  // Critical evidence gaps never block Generate. The old "N critical
  // evidence gaps detected — Continue with gaps / Add documents first"
  // dialog was removed (product decision, 2026-10-13): gaps are already
  // shown on the Evidence tab and carried into the report, so the
  // wizard goes straight to onConfirm. The audit events stay so
  // gap-carrying generations remain measurable.
  const handleConfirmWithGate = async () => {
    const critical = site.evidenceReadiness?.criticalGaps ?? [];
    if (critical.length > 0) {
      // Best-effort audit emission — the helper soft-fails when the
      // endpoint isn't deployed yet.
      void emitAuditEvent(preflightMode === 'first-run'
        ? {
            action: 'first-run-critical-gap-skip',
            target: { type: 'site', id: site.id, label: site.name },
            metadata: { gapCount: critical.length, reportType },
          }
        : {
            action: 'report.critical_gap_decision',
            target: { type: 'site', id: site.id, label: site.name },
            metadata: {
              decision: 'continued-without-prompt',
              gapCount: critical.length,
              gaps: critical.slice(0, 20),
              reportType,
            },
          });
    }
    await commitAndConfirm();
  };

  // Resolve the SED ref via a selector that returns a primitive — only
  // re-renders when THIS site's most-recent report sedId changes, not
  // whenever any report anywhere mutates. Fix_React_Error_185_Prompt
  // step 4. Without this, evidenceReadiness / unresolvedConflicts /
  // any sibling-site report write rode through the broad
  // `[..., allReports]` dep and re-fired the SED load.
  const latestSedRefId = useStore(s => {
    if (existingReport?.sedId) return existingReport.sedId;
    const reports = s.siteRag.reports
      .filter(r => reportBelongsToSite(site, r))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return reports[0]?.sedId ?? null;
  });

  // NWX-9 — the loader subscribes to `site.sedVersion` so it re-fires
  // when `topUpSiteSed` mutates the SED in place. Without this the
  // wizard's local `sed` snapshot stayed pinned to the pre-prefetch
  // value: site landing readiness climbed to "Good" but the wizard's
  // preview rows kept showing MISSING because the underlying SED
  // state was stale. We debounce by 250ms so a burst of section-
  // scoped top-ups completing within ~200ms coalesces into one
  // reload instead of N. The first ever load (no prior version)
  // fires immediately — the debounce only gates re-loads.
  const SED_VERSION_RELOAD_DEBOUNCE_MS = 250;
  const sedLoadedVersionRef = React.useRef<number | undefined>(undefined);
  useEffect(() => {
    let cancelled = false;
    if (!latestSedRefId) {
      setSed(null);
      setSedLoading(false);
      sedLoadedVersionRef.current = undefined;
      return;
    }
    const storagePath = latestSedRefId === site.sedId ? site.sedStoragePath : undefined;
    const fire = () => {
      setSedLoading(true);
      loadSedById(latestSedRefId, storagePath)
        .then(loaded => {
          if (cancelled) return;
          setSed(loaded);
          sedLoadedVersionRef.current = site.sedVersion;
        })
        .catch(() => { if (!cancelled) setSed(null); })
        .finally(() => { if (!cancelled) setSedLoading(false); });
    };
    // First-time load (no prior version recorded) OR a different SED
    // ref (`latestSedRefId` flipped) → fire immediately. A pure
    // `sedVersion` bump on the same ref → debounce so coalesced
    // top-ups don't thrash the loader.
    const versionBumpOnly = sedLoadedVersionRef.current !== undefined
      && sedLoadedVersionRef.current !== site.sedVersion;
    if (!versionBumpOnly) {
      fire();
      return () => { cancelled = true; };
    }
    const timer = setTimeout(fire, SED_VERSION_RELOAD_DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [latestSedRefId, site.sedId, site.sedStoragePath, site.sedVersion]);

  // First-screen welcome mode is distinct from later screening with disclosed gaps.
  const bootstrapMode = isFirstReportForSite(site, allReports);

  // Threading `wiredProviders` lets preflight keep a section red when
  // its primary provider isn't configured in this deployment, even in
  // bootstrap mode — admin needs to wire the provider before the run
  // can collect that section.
  const preflight: PreflightResult = runPreflight(
    sed,
    typeDef.requiredSedSections,
    { bootstrapMode, reportTypeId: reportType, wiredProviders: getEffectiveWiredProviders() },
  );

  // Next-version hint for the Generate button copy. The wizard mode + the
  // existing report's version determines the actual number when the run
  // commits — this label is just for the CTA.
  const nextVersionLabel = `v${(existingReport?.version ?? 0) + 1}`;
  // Documents_On_Site Prompt 12 — count unresolved conflicts on the
  // active SED so the Generate button can warn. Doesn't block.
  const unresolvedConflictCount = (() => {
    if (!sed) return 0;
    const list = (sed as { evidenceConflicts?: Array<import('../../services/evidenceConflict').EvidenceConflict> }).evidenceConflicts;
    return (list ?? []).filter(c => !c.userResolution).length;
  })();

  const generateButtonLabel = evidenceCollecting
    ? `Generate ${nextVersionLabel} — will queue after evidence completes`
    : unresolvedConflictCount > 0
      ? `Generate ${nextVersionLabel} (with ${unresolvedConflictCount} unresolved conflict warning${unresolvedConflictCount === 1 ? '' : 's'})`
      : `Generate ${nextVersionLabel}`;

  // Wizard + rerun panels share this dispatcher (preferences/index)
  // so adding a new report type only touches one switch.
  const siteReportsForSite = useMemo(
    () => allReports.filter(
      r => r.siteId === site.id || (site.reportIds ?? []).includes(r.id),
    ),
    [allReports, site.id, site.reportIds],
  );

  const preferencesFormExtraProps = {
    ...(reportType === 'custom-brief'
      ? {
          site: { id: site.id, savedCustomBriefs: site.savedCustomBriefs },
          onSaveCustomBrief: (prefs: import('../../types/siteRag').CustomBriefPreferences, existingId?: string) =>
            saveSiteCustomBrief(site.id, prefs, existingId),
          onDeleteCustomBrief: (briefId: string) => deleteSiteCustomBrief(site.id, briefId),
        }
      : {}),
    ...(reportType === 'executive-summary'
      ? { siteReports: siteReportsForSite }
      : {}),
  };

  const renderSubForm = () => (
    <div className="space-y-4">
      {isWizardInlineSetupType(reportType) ? (
          <>
            <WizardSetupReviewPanel
              reportType={reportType}
              site={site}
              onOpenSiteIntent={onOpenSiteIntent}
              onOpenCoverageMatrix={onOpenCoverageMatrix}
              includeReportSetup={false}
            />
            <PreferencesFormFor
              reportType={reportType}
              value={prefs}
              onChange={setPrefs}
              {...preferencesFormExtraProps}
            />
          </>
        ) : (
            <WizardSetupReviewPanel
              reportType={reportType}
              site={site}
              sed={sed}
              onOpenSiteIntent={onOpenSiteIntent}
              onOpenCoverageMatrix={onOpenCoverageMatrix}
              setupFlushRef={setupFlushRef}
            />
        )}
    </div>
  );

  // Doc-drawer overlay — extracted so both the first-run early-return
  // and the normal render branch can mount it without JSX duplication.
  // The Upload affordance on FirstRunBootstrapCard flips
  // `docDrawerOpen` true; closing the overlay returns focus to the
  // welcome card with `site.documents` already updated.
  const docDrawerOverlay = docDrawerOpen ? (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" data-testid="wizard-doc-drawer">
      <div className="bg-background max-w-[min(95vw,680px)] w-full max-h-[80vh] flex flex-col shadow-2xl border border-border" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <h3 className="text-sm font-bold uppercase tracking-widest text-foreground">Add documents</h3>
            <p className="type-meta text-text-secondary uppercase tracking-widest mt-0.5">
              {(site.documents ?? []).filter(d => !isLibraryIndexDoc(d)).length} document(s) in library
            </p>
          </div>
          <button
            onClick={() => setDocDrawerOpen(false)}
            className="text-[10px] underline text-text-muted hover:text-text-primary"
            data-testid="wizard-doc-drawer-close"
          >
            Done
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <DocumentLibrary
            documents={(site.documents ?? []).filter(d => !isLibraryIndexDoc(d))}
            siteId={site.id}
            open={true}
            embedded={true}
            onClose={() => setDocDrawerOpen(false)}
            onUpdateDocuments={async (updated) => {
              await useStore.getState().updateSiteDocuments(site.id, updated);
            }}
            recommendedReportTypes={[reportType]}
            onSkipUploads={() => setDocDrawerOpen(false)}
          />
        </div>
      </div>
    </div>
  ) : null;

  // Preflight Prompt 3 — first-run early return. With zero reports and
  // no SED, the wizard renders ONE welcoming card with ONE primary
  // action. Custom Brief is exempt: users must define brief name +
  // questions before generation, so we always surface the preferences
  // form instead of the welcome card.
  if (preflightMode === 'first-run' && reportType !== 'custom-brief' && reportType !== 'executive-summary') {
    return (
      <div data-testid="report-preferences-body">
        <div className="p-5">
          <FirstRunBootstrapCard
            site={site}
            reportType={reportType}
            onGenerate={handleConfirmWithGate}
            onOpenDocLibrary={() => setDocDrawerOpen(true)}
            docCount={docsCount}
          />
        </div>
        {docDrawerOverlay}
      </div>
    );
  }

  return (
    <div data-testid="report-preferences-body">
      {/* Per-step header — WizardFrame provides the wizard title +
           step strip. The legacy modal wrapper + per-step Back button
           live on WizardFrame now (Workflow Prompt 3); this header
           keeps the step-specific context (icon + report-type label
           + "updating existing" pill). */}
      <div className="px-5 py-4 border-b border-border">
        <div className="flex items-start gap-3">
            <div className="w-10 h-10 border border-border bg-state-hover flex items-center justify-center shrink-0">
              <Icon className="w-5 h-5 text-text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-sm font-bold uppercase tracking-widest text-text-primary">{typeDef.label}</h2>
                <span className={`type-meta font-bold uppercase tracking-widest px-1.5 py-0.5 border ${
                  banner.tone === 'warning'
                    ? 'bg-warning/15 text-warning border-warning/40'
                    : 'selected-accent border'
                }`}>
                  {banner.label}
                </span>
              </div>
              <div className="text-[10px] text-text-secondary mt-0.5">{site.name} — {site.address}</div>
              {existingReport && (
                <div className="text-[10px] text-text-muted mt-1 italic">
                  Last run {new Date(existingReport.createdAt).toLocaleDateString()} (v{existingReport.version ?? 1}.{existingReport.minorVersion ?? 0}).
                </div>
              )}
              {/* Workflow Prompt 6 — seed source subtitle. Tells the
                   user where the prefilled values came from so an
                   add-another fork doesn't surprise them with sibling
                   values that look like fresh defaults. The Reset
                   link drops back to intent-seeded values — the
                   wizard pre-computed `intentDefaults` so this is a
                   single-step revert. */}
              {!screeningRerun && (
                <div
                  className="text-[10px] text-text-muted mt-1 flex items-center gap-2 flex-wrap"
                  data-testid="prefs-seed-subtitle"
                >
                  <span>
                    {seedSource === 'sibling' && siblingReport ? (
                      <>Pre-filled from your previous {typeDef.shortLabel} report (
                        {new Date(siblingReport.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}).
                      </>
                    ) : seedSource === 'parent' && existingReport ? (
                      <>Pre-filled from {typeDef.shortLabel} v{existingReport.version ?? 1}.{existingReport.minorVersion ?? 0}.</>
                    ) : (
                      <>Pre-filled from site project intent.</>
                    )}
                  </span>
                  {seedSource !== 'intent' && intentDefaults && (
                    <button
                      type="button"
                      onClick={() => {
                        setPrefs(intentDefaults);
                      }}
                      className="text-[10px] text-primary underline hover:text-primary/80"
                      data-testid="prefs-reset-to-defaults"
                    >
                      Reset to site defaults
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Failed-SED banner — explicit recovery path. Without this the
            preflight goes red with no useful error context, and the user
            doesn't know to hit Retry on the SedStatusPill. While retry
            is in flight (sedStatus 'bootstrapping' or local isRetrying),
            the copy + buttons swap to the in-flight state; once
            sedStatus flips back to 'partial' or 'complete', the
            useEffect above clears isRetrying and the banner stops
            rendering. */}
        {(sedFailed || isRetrying) && (
          <div
            data-testid="failed-sed-banner"
            data-state={isRetrying ? 'retrying' : 'failed'}
            className="mx-5 my-4 border border-danger/40 bg-danger/10 p-4 space-y-2"
          >
            <div className="flex items-start gap-2">
              {isRetrying ? (
                <Loader2 className="w-4 h-4 text-warning shrink-0 mt-0.5 animate-spin" />
              ) : (
                <AlertTriangle className="w-4 h-4 text-danger shrink-0 mt-0.5" />
              )}
              <div className="flex-1 space-y-1">
                <h3 className="type-section text-danger">
                  {isRetrying ? 'Retrying evidence collection' : 'Evidence collection failed'}
                </h3>
                <p className="text-[10px] text-text-secondary leading-relaxed">
                  {isRetrying
                    ? 'Retrying… this can take 30–90 seconds.'
                    : "We couldn't bootstrap site evidence. Without it, report quality will be poor."}
                </p>
                {!isRetrying && site.sedFailureReason && (
                  <p className="type-body text-text-muted font-mono leading-relaxed">
                    {site.sedFailureReason}
                  </p>
                )}
              </div>
            </div>
            {/* Workflow Prompt 12 — wrap so the buttons stack on
                 narrow viewports (380px) instead of overflowing the
                 banner. `w-full sm:w-auto` makes each button
                 full-width below the sm breakpoint. */}
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                className="h-8 type-body uppercase tracking-widest w-full sm:w-auto"
                disabled={isRetrying}
                onClick={() => void handleSedRetry()}
              >
                <RefreshCw className={`w-3 h-3 mr-1 ${isRetrying ? 'animate-spin' : ''}`} />
                {isRetrying ? 'Retrying…' : 'Retry evidence collection'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 type-body uppercase tracking-widest w-full sm:w-auto"
                disabled={isRetrying}
                onClick={onBack}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        <SedCrossTabWaitBanner site={site} />

        {/* R-32 — recent bootstrap freshness notice (wizard preflight). */}
        {sedBootstrapFreshness.fresh && site.lastBootstrapAt && !sedFailed && !evidenceCollecting && (
          <div
            data-testid="sed-bootstrap-skipped-fresh-notice"
            className="mx-5 my-3 rounded-md border border-success/30 bg-success/5 px-4 py-3 text-[10px] text-text-secondary"
          >
            SED is current (rebuilt {formatRelativeTime(site.lastBootstrapAt)})
          </div>
        )}

        {/* Body — sub-form */}
        {!screeningRerun && <div className="p-5">{renderSubForm()}</div>}

        {/* Generation-quality picker REMOVED (William, 2026-06-16): the Draft
             tier is no longer offered in the UI — every report generates at the
             investment-grade ("full") tier, the deliverable. `generationTier`
             stays pinned to DEFAULT_GENERATION_TIER (investment-grade) and is
             still threaded through onConfirm → siteConfigOverrides.generationTier
             so the pipeline behaviour is unchanged. The downstream draft
             safety (the report header's "Draft" status + Finalize action, and
             the export-bar) is retained for any pre-existing draft reports,
             but no new draft can be produced from here. */}

        {/* Cross-field warnings (Workflow Prompt 11). Errors block
             Generate; warns are advisory. Rendered above the footer
             so the user sees them before reaching the CTA. */}
        {crossWarnings.length > 0 && (
          <div className="px-5 py-2 border-t border-border-subtle space-y-1" data-testid="prefs-cross-warnings">
            {crossErrors.map((w, i) => (
              <div key={`err-${i}`} className="text-[10px] text-danger flex items-start gap-1.5">
                <span className="font-bold uppercase tracking-widest shrink-0">
                  {labelFor(reportType, w.field)}:
                </span>
                <span>{w.message}</span>
              </div>
            ))}
            {crossWarnsOnly.map((w, i) => (
              <div key={`warn-${i}`} className="text-[10px] text-warning flex items-start gap-1.5">
                <span className="font-bold uppercase tracking-widest shrink-0">
                  {labelFor(reportType, w.field)}:
                </span>
                <span>{w.message}</span>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border flex items-center justify-between">
          <div className="text-[10px]">
            {!validation.valid && (
              <div className="text-warning">
                Required: {validation.missingRequired.join(', ')}
              </div>
            )}
            {crossErrors.length > 0 && (
              <div className="text-danger">
                {crossErrors.length} cross-field error{crossErrors.length === 1 ? '' : 's'} — fix before generating.
              </div>
            )}
          </div>
          {/* CTA cluster — Back on the left, Generate on the right. */}
          <div className="flex gap-2 flex-wrap justify-end">
            <Button variant="outline" size="sm" className="h-8 type-body uppercase tracking-widest" onClick={onBack}>
              Back
            </Button>
            <Button
              size="sm"
              className="h-8 type-body uppercase tracking-widest"
              disabled={!validation.valid || crossErrors.length > 0 || sedLoading || blockGenerateForFailedSed}
              /* Every Generate click goes through handleConfirmWithGate so
                  the `report.critical_gap_decision` audit fires; it never
                  blocks on critical gaps. */
              onClick={() => void handleConfirmWithGate()}
              data-testid="wizard-generate-cta"
            >
              {generateButtonLabel}
            </Button>
          </div>
        </div>

      {/* Documents_On_Site Prompt 8-E — stacked DocumentLibrary drawer.
           Mounts at z-[60] so it sits above the wizard's z-50 backdrop;
           closing it returns focus to the wizard with all preferences
           intact. Persistence flows through the same updateSiteDocuments
           path as the regular Documents tab, so freshly-uploaded docs
           land in `site.documents` and re-flow into the
           DocumentSelectionPanel once the panel re-evaluates.
           JSX extracted to `docDrawerOverlay` above so the first-run
           early-return branch can reuse it. */}
      {docDrawerOverlay}
    </div>
  );
};
